import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Award, BookOpen, Target, CheckCircle, Tractor, Leaf } from 'lucide-react';

const BScAgricultureCoursePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/courses"
            className="inline-flex items-center text-green-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Courses
          </Link>
          <h1 className="text-4xl font-bold mb-4">B.Sc Agriculture - Bachelor of Science in Agriculture</h1>
          <p className="text-xl text-green-100 max-w-3xl">
            A comprehensive 4-year undergraduate program that combines science, technology, and business to prepare students for careers in modern agriculture and agribusiness.
          </p>
        </div>
      </div>

      {/* Course Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Overview</h2>
              <p className="text-gray-700 mb-4">
                Bachelor of Science in Agriculture (B.Sc Agriculture) is a professional undergraduate degree program that provides comprehensive knowledge about various aspects of agriculture, including crop production, soil management, pest control, agricultural economics, and modern farming techniques.
              </p>
              <p className="text-gray-700">
                The program combines theoretical knowledge with practical training, preparing students to become skilled agricultural professionals who can contribute to sustainable food production and rural development.
              </p>
            </div>

            {/* Course Structure */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Structure</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Duration & Structure</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      4 years (8 semesters)
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Semester-based curriculum
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Practical training included
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Subjects</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Agronomy & Crop Production</li>
                    <li>• Soil Science & Management</li>
                    <li>• Agricultural Economics</li>
                    <li>• Plant Pathology & Entomology</li>
                    <li>• Agricultural Engineering</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Career Opportunities */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Career Opportunities</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Government Sector</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Agricultural Officer</li>
                    <li>• Extension Officer</li>
                    <li>• Research Assistant</li>
                    <li>• Seed Certification Officer</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Private Sector</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Farm Manager</li>
                    <li>• Agri-business Executive</li>
                    <li>• Quality Control Officer</li>
                    <li>• Sales & Marketing (Agri-inputs)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility Criteria</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Academic Requirements</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 10+2 with Physics, Chemistry, Biology/Mathematics</li>
                    <li>• Minimum 50-60% aggregate marks</li>
                    <li>• Age: 17-23 years</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Entrance Exams</h3>
                  <p className="text-gray-700">Some universities may require entrance exams or merit-based admission</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Facts */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Facts</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">Duration: 4 Years</span>
                </div>
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">Mode: Full-time</span>
                </div>
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">Degree: B.Sc Agriculture</span>
                </div>
                <div className="flex items-center">
                  <Tractor className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">Practical Training: Included</span>
                </div>
              </div>
            </div>

            {/* Specializations */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Popular Specializations</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Agronomy</h4>
                  <p className="text-sm text-gray-600">Crop production and management</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Horticulture</h4>
                  <p className="text-sm text-gray-600">Fruit and vegetable cultivation</p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Agricultural Economics</h4>
                  <p className="text-sm text-gray-600">Farm business management</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Soil Science</h4>
                  <p className="text-sm text-gray-600">Soil health and fertility</p>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-lg p-6 text-white">
              <h3 className="text-xl font-bold mb-3">Need Guidance?</h3>
              <p className="mb-4">Get expert counseling for Agriculture admissions</p>
              <Link
                to="/premium-services"
                className="inline-block bg-white text-green-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Get Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BScAgricultureCoursePage;